import React, { useEffect, useId, useRef, useState } from "react";
import { CalciteTooltip } from "@esri/calcite-components-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type InfoIconWithTooltipProps = {
  icon: any;
  tooltip: string;
  className?: string;
};

export function InfoIconWithTooltip({ icon, tooltip, className }: InfoIconWithTooltipProps) {
  const iconId = useId();
  const tipId = useId();
  const [open, setOpen] = useState(false);

  // helps fix issue of screenreader reading tooltip infinitely while open
  const [announceNow, setAnnounceNow] = useState(false);
  const announceTimerRef = useRef<number | null>(null);

  const iconElRef = useRef<HTMLSpanElement | null>(null);
  const tooltipElRef = useRef<HTMLDivElement | null>(null);

  // ESC closes
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // One-time SR announcement
  useEffect(() => {
    if (open) {
      setAnnounceNow(true);
      announceTimerRef.current = window.setTimeout(() => setAnnounceNow(false), 400);
    } else if (announceTimerRef.current) {
      clearTimeout(announceTimerRef.current);
      announceTimerRef.current = null;
      setAnnounceNow(false);
    }
  }, [open]);

  // Outside click closes
  useEffect(() => {
    if (!open) return;
    const onDocPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      const hitIcon = iconElRef.current?.contains(t);
      const hitTip = (tooltipElRef.current as HTMLElement | null)?.contains?.(t) ?? false;
      if (!hitIcon && !hitTip) setOpen(false);
    };
    document.addEventListener("pointerdown", onDocPointerDown, true);
    return () => document.removeEventListener("pointerdown", onDocPointerDown, true);
  }, [open]);

  const toggle = () => setOpen((o) => !o);

  const handleClick: React.MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation(); // don’t click the parent button
    toggle();
  };

  const handleKeyDown: React.KeyboardEventHandler = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      toggle();
    }
  };

  const handleBlur: React.FocusEventHandler = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false);
  };

  return (
    <>
      <span
        ref={iconElRef}
        id={iconId}
        role="button"
        tabIndex={0}
        title={tooltip}
        aria-label="More info. Press Enter or Space to show details."
        aria-expanded={open}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className={className}
        style={{
          display: "inline-flex",
          alignItems: "center",
          marginLeft: 6,
          lineHeight: 1,
          verticalAlign: "middle",
        }}
      >
        <FontAwesomeIcon icon={icon} style={{ verticalAlign: "middle" }} />
      </span>

      {/* SR live announcement (short-lived) */}
      {announceNow && (
        <div aria-live="polite" className="sr-only">
          {tooltip}
        </div>
      )}

      {/* CALCITE TOOLTIP: mounted but DETACHED until open, so hover never triggers it */}
      <CalciteTooltip
        ref={tooltipElRef as any}
        open={open}
        hidden={!open}
        referenceElement={open ? iconId : undefined} // <-- attach only when open
        overlayPositioning="fixed"
        closeOnClick
        style={{ ["--calcite-tooltip-max-size-x" as any]: "28rem" } as React.CSSProperties}
      >
        {/* Visual only to avoid SR re-reads */}
        <div
          id={tipId}
          role="tooltip"
          aria-hidden="true"
          style={{ maxWidth: "28rem", whiteSpace: "normal", overflowWrap: "anywhere" }}
        >
          {tooltip}
        </div>
      </CalciteTooltip>
    </>
  );
}
